import { useNavigate } from "react-router-dom";

/**
 * Direcciones de temas que se comparten por fuera del sistema.
 *
 * La ficha de un tema vive en dos rutas: `/temas/:id` (sitio público) y
 * `/app/temas/:id` (dentro de la app, detrás del login). Solo la primera se abre
 * sin sesión, así que es la que se copia al compartir y a la que se manda a
 * quien llega sin sesión con un link de la app.
 */

/** Base del SPA sin la barra final: "/titulacion" en producción, "" en desarrollo. */
function baseSpa(): string {
  const base: string = (import.meta as any).env?.BASE_URL || "/";
  return base === "/" ? "" : base.replace(/\/$/, "");
}

/** Dirección absoluta de un tema, la que se puede pegar en un correo. */
export function urlPublicaDeTema(id: number | string): string {
  return `${window.location.origin}${baseSpa()}/temas/${id}`;
}

/**
 * Ruta pública equivalente a una de la app, o `null` si no hay ninguna. Con esto
 * los links a `/app/temas/313` que ya andan circulando siguen sirviendo para
 * quien no tiene sesión, en vez de dejarlo en el login.
 */
export function rutaPublicaEquivalente(pathname: string): string | null {
  const tema = pathname.match(/^\/app\/temas(\/\d+)?\/?$/);
  return tema ? `/temas${tema[1] ?? ""}` : null;
}

/** Lo único que se mira de un evento para saber si pide otra pestaña. */
export type GestoDeApertura = { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number };

/**
 * ¿Este clic pide abrir en otra pestaña?
 *
 * Ctrl (o ⌘ en Mac), Shift, o el botón central: lo que el navegador ya hace solo
 * con un `<a>`. Las filas y tarjetas del catálogo no son enlaces —una `<tr>` no
 * puede ir dentro de un `<a>` sin romper la tabla— así que el gesto hay que
 * reconocerlo a mano donde se navega con `useNavigate`; para eso está `useIr`.
 */
export function pideOtraPestana(e: GestoDeApertura): boolean {
  return !!(e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1);
}

/** Abre una ruta del SPA en otra pestaña, respetando el base path del build. */
export function abrirEnOtraPestana(ruta: string): void {
  window.open(`${baseSpa()}${ruta}`, "_blank", "noopener");
}

/**
 * Navegar respetando el gesto de "abrir en otra pestaña".
 *
 * Devuelve `ir(e, ruta)` para el `onClick` de lo que no es un enlace de verdad
 * —filas, tarjetas, botones—: con Ctrl/⌘ abre en otra pestaña y deja la pantalla
 * donde está; si no, navega como siempre. Donde sí se puede poner un `<a>`
 * (`EnlaceTema`) no hace falta: el navegador ya lo hace solo.
 */
export function useIr() {
  const navigate = useNavigate();
  return (e: GestoDeApertura, ruta: string) => (pideOtraPestana(e) ? abrirEnOtraPestana(ruta) : navigate(ruta));
}
